/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file 异常堆栈相关处理函数
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */
import { __values } from "tslib";
import { joinRegExp } from './utils/misc';
/**
 * 检测本帧是否是当前文件帧，例如 CDN 文件地址
 */
function isSelfFrame(frame) {
    var filename = frame.filename || '';
    // 如果是从 CDN 获取
    var isFromCDN = filename.indexOf('weirwood-sdk') !== -1;
    // 如果是敏感函数
    var isSensitiveFunction = /captureException$/.test(filename);
    return isFromCDN || isSensitiveFunction;
}
/**
 * 规范化异常堆栈列表
 * @param stackInfo Tracekit 计算出来的错误信息
 * @param options 选项
 * @param limit 限制堆栈帧数量
 */
export function prepareFrames(stackInfo, options, limit) {
    var e_1, _a;
    var frames = [];
    if (stackInfo.stack && stackInfo.stack.length) {
        try {
            for (var _b = __values(stackInfo.stack), _c = _b.next(); !_c.done; _c = _b.next()) {
                var stack = _c.value;
                var frame = normalizeFrame(stack, stackInfo.url || '', options.includePaths);
                frames.push(frame);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
        // e.g. frames captured via captureMessage throw
        // 如果使用 captureMessage 手动触发，需要设置头部帧不是由 app 内部代码触发
        if (options && options.trimHeadFrames) {
            for (var j = 0; j < options.trimHeadFrames && j < frames.length; j++) {
                frames[j].in_app = false;
            }
        }
    }
    frames = frames.filter(function (f) { return !isSelfFrame(f); });
    frames = frames.slice(0, limit);
    return frames;
}
/**
 * 格式化异常堆栈帧，将 Tracekit 收集到的帧格式转化为 StackFrame，也即 StackInfoFrame -> StackFrame
 * @param frame
 * @param stackInfoUrl
 */
export function normalizeFrame(frame, stackInfoUrl, includePaths) {
    // normalize the frames data
    var normalized = {
        // Case when we don't have any information about the error
        // E.g. throwing a string or raw object, instead of an `Error` in Firefox
        // Generating synthetic error doesn't add any value here
        //
        // We should probably somehow let a user know that they should fix their code
        filename: frame.url || stackInfoUrl,
        lineno: frame.line,
        colno: frame.column,
        function: frame.func || '?'
    };
    // 处理 in_app 逻辑
    if (includePaths && includePaths.length > 0) {
        var regex = joinRegExp(includePaths);
        var filenameMatch = regex.test(normalized.filename);
        var functionMatch = /(Weirwood|Tracekit)/.test(normalized['function']);
        var minifileMatch = /weirwood\-sdk/.test(normalized.filename);
        normalized.in_app = filenameMatch;
        if (functionMatch || minifileMatch) {
            normalized.in_app = false;
        }
    }
    else {
        normalized.in_app = false;
    }
    return normalized;
}
/**
 * 处理堆栈，并返回
 * 1. 如果有堆栈，将堆栈倒序
 * 2. 如果没有堆栈，将 fileurl 和 lineno 拼接成一个堆栈帧
 * @param frames 格式化好的堆栈
 * @param fileurl 错误来源
 * @param lineno 错误行
 * @returns Array<StackFrame>
 */
export function computeFrames(frames, fileurl, lineno) {
    var hasFrames = frames && frames.length;
    if (hasFrames) {
        // Sentry expects frames oldest to newest
        // and JS sends them as newest to oldest
        return frames.reverse();
    }
    else if (fileurl) {
        return [{
                filename: fileurl,
                lineno: lineno || 0,
                in_app: true
            }];
    }
    return [];
}
/**
 * 获取错误来源，如果有堆栈，返回第一帧的 filename，否则返回传入的 fileurl
 * @param frames 格式化好的堆栈
 * @param fileurl 错误来源
 * @returns string
 */
export function computeTransactionUrl(frames, fileurl) {
    var hasFrames = frames && frames.length;
    if (hasFrames) {
        return frames[0].filename || fileurl;
    }
    return fileurl;
}
//# sourceMappingURL=stackframe.js.map